if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.updateQuantityState();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      updateQuantityState() {
        const quantityInput = this.querySelector('quantity-input');
        if (!quantityInput) return;

        const productSubmitButton = this.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        const isSoldOut = productSubmitButton ? productSubmitButton.hasAttribute('disabled') : true;

        if (isSoldOut) {
            quantityInput.classList.add('disabled');
            quantityInput.querySelectorAll('input, button').forEach(el => el.setAttribute('disabled', 'disabled'));
        } else {
            quantityInput.classList.remove('disabled');
            quantityInput.querySelectorAll('input, button').forEach(el => el.removeAttribute('disabled'));
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.syncCartQuantity.bind(this));
        }

        this.initCartSync();
      }

      initCartSync() {
        const quantityInputEl = this.querySelector('quantity-input');
        if (!quantityInputEl || !quantityInputEl.hasAttribute('data-sync-cart-quantity')) return;

        this.cartSyncDebounceTimer = null;
        this.cartSyncDebouncedUpdate = (newQuantity) => {
          clearTimeout(this.cartSyncDebounceTimer);
          this.cartSyncDebounceTimer = setTimeout(() => this.updateCartQuantity(newQuantity), ON_CHANGE_DEBOUNCE_TIMER);
        };

        if (this._syncButtonListeners) {
          this._syncButtonListeners.forEach(({ el, handler }) => el.removeEventListener('click', handler));
        }

        this._syncButtonListeners = [];
        const boundHandler = this.onSyncButtonClick.bind(this);
        quantityInputEl.querySelectorAll('button').forEach((button) => {
          button.addEventListener('click', boundHandler);
          this._syncButtonListeners.push({ el: button, handler: boundHandler });
        });

        this.updateMinusButtonIcon();
      }

      onSyncButtonClick(event) {
        const cartQuantity = parseInt(this.quantityInput.dataset.cartQuantity) || 0;
        const button = event.target.closest('button');
        const isPlusButton = button?.name === 'plus';

        if (cartQuantity === 0) {
          if (isPlusButton) {
            clearTimeout(this.cartSyncDebounceTimer);
            const min = parseInt(this.quantityInput.min) || 1;
            this.quantityInput.value = min;
            this.addToCart(min);
          }
          return;
        }

        if (!isPlusButton) {
          const minusBtn = this.querySelector('quantity-input button[name="minus"]');
          const trashIcon = minusBtn?.querySelector('.qty-icon--trash');
          if (trashIcon && !trashIcon.classList.contains('hidden')) {
            clearTimeout(this.cartSyncDebounceTimer);
            this.quantityInput.value = 1;
            this.updateCartQuantity(0);
            return;
          }
        }

        const newQuantity = parseInt(this.quantityInput.value);
        this.updateMinusButtonIcon();
        this.cartSyncDebouncedUpdate(newQuantity);
      }

      addToCart(quantity) {
        const variantId = this.productForm?.variantIdInput?.value;
        if (!variantId) return;
        if (CartThrottle.blocked()) return;

        this.applyCartSyncState(quantity, quantity);
        this.updateCartBubbleCount(1);
        this.publishCartSync(variantId, null, quantity, window.cartStrings.itemAdded);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', quantity);
        formData.append('sections', this.cartSyncSections);
        formData.append('sections_url', window.location.pathname);
        config.body = formData;

        this.cartSyncWithLoading((done) => {
          fetch(`${routes.cart_add_url}`, config)
            .then((response) => {
              if (CartThrottle.checkMutation(response)) {
                this.rollbackCartSync(0, null, -1);
                return null;
              }
              return response.json();
            })
            .then((response) => {
              if (!response) return;
              if (response.status) {
                console.error(response.description || response.errors);
                this.rollbackCartSync(0, null, -1, window.cartStrings.cartUpdateError);
                return;
              }

              const actualQty = response.quantity || quantity;
              if (actualQty !== quantity) this.applyCartSyncState(actualQty, actualQty);
              this.renderCartSections(response.sections);
            })
            .catch((e) => {
              console.error(e);
              this.rollbackCartSync(0, null, -1);
            })
            .finally(done);
        });
      }

      updateCartQuantity(quantity) {
        const variantId = this.productForm?.variantIdInput?.value;
        if (!variantId) return;

        const previousCartQty = parseInt(this.quantityInput.dataset.cartQuantity) || 0;
        if (CartThrottle.blocked()) {
          this.applyCartSyncState(previousCartQty, previousCartQty === 0 ? null : previousCartQty);
          return;
        }
        const qtyDelta = quantity - previousCartQty;

        this.applyCartSyncState(quantity, quantity === 0 ? null : quantity);
        this.updateCartBubbleCount(qtyDelta);

        let optimisticToast;
        if (quantity === 0) optimisticToast = window.cartStrings.itemRemoved;
        else if (previousCartQty === 0) optimisticToast = window.cartStrings.itemAdded;
        else optimisticToast = window.cartStrings.itemUpdated;
        this.publishCartSync(variantId, null, quantity, optimisticToast);

        const body = JSON.stringify({
          id: variantId,
          quantity: quantity,
          sections: this.cartSyncSections,
          sections_url: window.location.pathname,
        });

        this.cartSyncWithLoading((done) => {
          fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
            .then((response) => {
              if (CartThrottle.checkMutation(response)) {
                this.rollbackCartSync(previousCartQty, previousCartQty === 0 ? null : previousCartQty, -qtyDelta);
                return null;
              }
              return response.json();
            })
            .then((parsedState) => {
              if (!parsedState) return;
              if (parsedState.errors) {
                console.error(parsedState.errors);
                this.rollbackCartSync(previousCartQty, previousCartQty === 0 ? null : previousCartQty, -qtyDelta, window.cartStrings.cartUpdateError);
                return;
              }

              const updatedItem = parsedState.items.find((item) => String(item.variant_id) === String(variantId));
              const actualQty = updatedItem ? updatedItem.quantity : 0;

              if (actualQty !== quantity) {
                this.applyCartSyncState(actualQty, actualQty === 0 ? null : actualQty);
                this.updateCartBubbleCount(actualQty - quantity);
              }
              this.renderCartSections(parsedState.sections, parsedState.item_count);
              publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
            })
            .catch((e) => {
              console.error(e);
              this.rollbackCartSync(previousCartQty, previousCartQty === 0 ? null : previousCartQty, -qtyDelta);
            })
            .finally(done);
        });
      }

      // --- Cart Sync shared helpers ---

      get cartSyncSections() {
        return ['cart-icon-bubble'];
      }

      updateMinusButtonIcon() {
        const quantityInputEl = this.querySelector('quantity-input');
        if (!quantityInputEl || !quantityInputEl.hasAttribute('data-sync-cart-quantity')) return;

        const minusBtn = quantityInputEl.querySelector('button[name="minus"]');
        if (!minusBtn) return;

        const minusIcon = minusBtn.querySelector('.qty-icon--minus');
        const trashIcon = minusBtn.querySelector('.qty-icon--trash');
        if (!minusIcon || !trashIcon) return;

        const currentValue = parseInt(this.quantityInput.value) || 0;
        const cartQuantity = parseInt(this.quantityInput.dataset.cartQuantity) || 0;
        const showTrash = cartQuantity > 0 && currentValue <= 1;

        minusIcon.classList.toggle('hidden', showTrash);
        trashIcon.classList.toggle('hidden', !showTrash);
      }

      cartSyncWithLoading(fn) {
        const quantityInputEl = this.querySelector('quantity-input');
        quantityInputEl.classList.add('loading');
        fn(() => quantityInputEl.classList.remove('loading'));
      }

      applyCartSyncState(cartQty, inputValue) {
        this.quantityInput.dataset.cartQuantity = cartQty;
        if (inputValue === null) {
          this.quantityInput.value = parseInt(this.quantityInput.dataset.min) || 1;
        } else if (inputValue !== undefined) {
          this.quantityInput.value = inputValue;
        }
        this.updateMinusButtonIcon();
      }

      // The drawer is re-rendered from the server by cart-drawer.js rather than from
      // the bundled section: bundled Cart API renders can lack the customer session.
      renderCartSections(sections, itemCount) {
        this.cartSyncSections.forEach((sectionId) => {
          const sectionHtml = sections?.[sectionId];
          if (!sectionHtml) return;

          const htmlDoc = new DOMParser().parseFromString(sectionHtml, 'text/html');

          if (sectionId === 'cart-icon-bubble') {
            const iconBubble = document.getElementById('cart-icon-bubble');
            const newIconBubble = htmlDoc.querySelector('.shopify-section');
            if (iconBubble && newIconBubble) iconBubble.innerHTML = newIconBubble.innerHTML;
          }
        });

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.scheduleRefresh === 'function') {
          cartDrawer.scheduleRefresh(itemCount);
        } else if (cartDrawer && itemCount !== undefined) {
          cartDrawer.classList.toggle('is-empty', itemCount === 0);
        }

        const cartItems = document.querySelector('cart-items');
        if (cartItems && typeof cartItems.refreshFromServer === 'function') cartItems.refreshFromServer();
      }

      rollbackCartSync(cartQty, inputValue, bubbleDelta, errorMessage) {
        this.applyCartSyncState(cartQty, inputValue);
        this.updateCartBubbleCount(bubbleDelta);
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-info-sync',
          productVariantId: this.productForm?.variantIdInput?.value,
          quantity: cartQty,
          toastMessage: CartThrottle.suspended ? undefined : errorMessage || window.cartStrings.networkError,
          isError: true,
        });
      }

      updateCartBubbleCount(delta) {
        if (delta === 0) return;
        const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden]');
        if (bubble) {
          const current = parseInt(bubble.textContent) || 0;
          const newCount = Math.max(0, current + delta);
          bubble.textContent = newCount;
        } else if (delta > 0) {
          const iconBubble = document.getElementById('cart-icon-bubble');
          if (iconBubble) {
            const div = document.createElement('div');
            div.className = 'cart-count-bubble';
            const span = document.createElement('span');
            span.setAttribute('aria-hidden', 'true');
            span.textContent = delta;
            div.appendChild(span);
            iconBubble.appendChild(div);
            const svgWrapper = iconBubble.querySelector('.svg-wrapper');
            if (svgWrapper) {
              const emptyIcon = svgWrapper.querySelector('svg');
              if (emptyIcon) svgWrapper.innerHTML = document.querySelector('#cart-icon-bubble .svg-wrapper')?.innerHTML || svgWrapper.innerHTML;
            }
          }
        }
      }

      publishCartSync(variantId, cartData, quantity, toastMessage) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-info-sync',
          productVariantId: variantId,
          cartData: cartData,
          quantity: quantity,
          toastMessage: toastMessage,
        });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        clearTimeout(this.cartSyncDebounceTimer);
        if (this._syncButtonListeners) {
          this._syncButtonListeners.forEach(({ el, handler }) => el.removeEventListener('click', handler));
          this._syncButtonListeners = null;
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').textContent = html.querySelector('head title').textContent;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              return;
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('AlternateProducts', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          this.updateQuantityState();

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        const currentMediaGallery = this.querySelector(`media-gallery`);
        const updatedMediaGallery = html.querySelector(`media-gallery`);
        if (currentMediaGallery && updatedMediaGallery) {
          currentMediaGallery.toggleAttribute(
            'data-hide-carton-media',
            updatedMediaGallery.hasAttribute('data-hide-carton-media')
          );
        }

        currentMediaGallery?.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }

        const syncEnabled = this.quantityInput.closest('quantity-input')?.hasAttribute('data-sync-cart-quantity');
        this.quantityInput.value = syncEnabled && data.cartQuantity > 0 ? data.cartQuantity : min;

        this.updateMinusButtonIcon();
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      // Quantity rules (min/max/step) belong to the variant and do not change with the cart, so a
      // cart event only needs to refresh the in-cart count. It is read from the event payload instead
      // of re-fetching the product section: one less GET per cart mutation while a PDP is open.
      syncCartQuantity(event) {
        if (event?.source === 'product-info-sync') return;
        if (!this.quantityInput || !this.quantityForm) return;

        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        const cartQuantity = this.cartQuantityFromEvent(event, currentVariantId);
        if (cartQuantity === null) return;

        this.quantityInput.dataset.cartQuantity = cartQuantity;

        const rulesCart = this.quantityForm.querySelector('.quantity__rules-cart');
        if (rulesCart) {
          rulesCart.classList.toggle('hidden', cartQuantity === 0);
          const count = rulesCart.querySelector('.quantity-cart');
          if (count) count.textContent = cartQuantity;
        }

        this.setQuantityBoundries();
      }

      // /cart/change and /cart/update responses carry the full item list.
      cartQuantityFromEvent(event, variantId) {
        const cartData = event?.cartData;
        if (!cartData) return null;

        if (Array.isArray(cartData.items)) {
          return cartData.items
            .filter((item) => String(item.variant_id) === String(variantId))
            .reduce((total, item) => total + item.quantity, 0);
        }

        if (String(cartData.variant_id) === String(variantId) && typeof cartData.quantity === 'number') {
          return cartData.quantity;
        }

        return null;
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }

        this.setQuantityBoundries();
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
